<template>
  <v-card variant="outlined" class="mb-4">
    <!-- Title -->
    <v-card-title class="d-flex align-center py-2 px-4 section-header">
      <v-icon size="20" class="mr-2">mdi-account-group</v-icon>
      <span class="text-subtitle-1 font-weight-bold">{{ sectionLabel || tl('Officer List') }}</span>
      <v-chip v-if="fetched && !loading" size="x-small" color="info" variant="tonal" class="ml-2">
        {{ directors.length }}
      </v-chip>
    </v-card-title>

    <v-divider />

    <v-card-text class="pa-3">
      <!-- Loading -->
      <div v-if="loading" class="d-flex align-center justify-center py-6">
        <v-progress-circular indeterminate color="primary" size="28" width="3" class="mr-3" />
        <span class="text-body-2 text-medium-emphasis">{{ tl('Loading') }} {{ officerTypeLabel }}...</span>
      </div>

      <!-- Error -->
      <v-alert v-else-if="error" type="error" variant="tonal" density="compact" class="mb-0">
        <div class="d-flex align-center justify-space-between">
          <span>{{ error }}</span>
          <v-btn size="small" variant="text" color="error" @click="emit('retry')">
            <v-icon start size="16">mdi-refresh</v-icon>
            {{ tl('Retry') }}
          </v-btn>
        </div>
      </v-alert>

      <!-- No data -->
      <v-alert v-else-if="fetched && directors.length === 0" type="info" variant="tonal" density="compact" class="mb-0">
        {{ tl('No officers found for this company.') }}
      </v-alert>

      <!-- Officer table header + accordion -->
      <template v-else-if="fetched && directors.length > 0">
        <!-- SSM-style table header -->
        <div class="officer-table-header">
          <div class="th-updated">{{ tl('Updated') }}</div>
          <div class="th-name">{{ tl('Name') }}</div>
          <div class="th-id">{{ tl('Identification Number') }}</div>
        </div>

        <!-- Officer rows (accordion) -->
        <v-expansion-panels v-model="openPanels" variant="accordion" class="officer-panels">
          <v-expansion-panel
            v-for="(director, idx) in directors"
            :key="director.officerIdx || idx"
            class="officer-panel"
          >
            <v-expansion-panel-title class="officer-panel-title" :class="{ 'officer-panel-title--dirty': isDirty(director.officerIdx) }">
              <div class="officer-row">
                <div class="td-updated">
                  <v-chip
                    :color="isDirty(director.officerIdx) ? 'warning' : 'default'"
                    :variant="isDirty(director.officerIdx) ? 'flat' : 'outlined'"
                    size="x-small"
                    class="status-chip"
                  >
                    <template v-if="isDirty(director.officerIdx)">
                      <v-icon start size="12">mdi-pencil</v-icon>
                      {{ tl('Yes') }}
                    </template>
                    <template v-else>
                      <v-icon start size="12">mdi-close</v-icon>
                      {{ tl('No') }}
                    </template>
                  </v-chip>
                </div>
                <div class="td-name">
                  <div class="font-weight-medium text-body-2">{{ director.fullName || tl('N/A') }}</div>
                  <div v-if="director.positionName" class="text-caption text-medium-emphasis">{{ director.positionName }}</div>
                </div>
                <div class="td-id">
                  <span class="text-body-2 font-weight-medium">{{ director.contactRegNo || '' }}</span>
                </div>
              </div>
            </v-expansion-panel-title>

            <v-expansion-panel-text class="officer-panel-content">
              <component
                :is="editFormComponent"
                :director="director"
                :read-only="readOnly"
                :section-action="sectionAction"
                @update:form-data="(data) => handleFormDataUpdate(director.officerIdx, data)"
              />
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>
      </template>
    </v-card-text>
  </v-card>
</template>

<script setup lang="ts">
import { ref, computed, type Component } from 'vue';
import { useTranslation } from '@/utils/locales/messages';
import { useLanguageStore } from '@/shared/stores/languageStore';
import type { LettagaDirector } from './composables/useDirectorList';
import type { DirectorFormData } from './forms/DirectorEditForm.vue';

// Translation
const languageStore = useLanguageStore();
const { createSyncT } = useTranslation();
const t = createSyncT(languageStore.currentLanguage, { silent: true });
const tl = (text: string) => {
  if (!text) return '';
  const translated = t(`startTask.${text}`);
  return (translated && translated !== `startTask.${text}`) ? translated : text;
};

interface Props {
  directors: LettagaDirector[];
  loading: boolean;
  error: string;
  fetched: boolean;
  readOnly?: boolean;
  directorFormDataMap: Record<string, DirectorFormData>;
  sectionLabel?: string;
  officerType?: string;
  sectionAction?: string;
  editFormComponent: Component;
}

const props = withDefaults(defineProps<Props>(), {
  readOnly: false,
  sectionLabel: '',
  officerType: 'DIRECTOR',
  sectionAction: '',
});

const emit = defineEmits<{
  'update:directorData': [officerIdx: string, data: DirectorFormData];
  retry: [];
}>();

const openPanels = ref<number[]>([]);

const officerTypeLabel = computed(() => {
  const upper = (props.officerType || '').toUpperCase();
  if (upper === 'MANAGER') return tl('managers');
  if (upper === 'SECRETARY') return tl('secretaries');
  return tl('directors');
});

function isDirty(officerIdx: string): boolean {
  return props.directorFormDataMap[officerIdx]?._isDirty || false;
}

function handleFormDataUpdate(officerIdx: string, data: DirectorFormData) {
  emit('update:directorData', officerIdx, data);
}
</script>

<style scoped>
.section-header {
  background: rgba(var(--v-theme-primary), 0.04);
}

.officer-table-header {
  display: grid;
  grid-template-columns: 90px 1fr 1fr;
  gap: 0;
  background: #3d4856;
  color: #fff;
  padding: 10px 16px;
  font-weight: 600;
  font-size: 0.78rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  border-radius: 4px 4px 0 0;
}

.officer-panels {
  border: 1px solid #e0e0e0;
  border-top: none;
  border-radius: 0 0 4px 4px;
}

.officer-panel {
  border-bottom: 1px solid #e8e8e8;
}

.officer-panel:last-child {
  border-bottom: none;
}

:deep(.officer-panel .v-expansion-panel-title) {
  padding: 8px 16px !important;
  min-height: 48px;
}

.officer-panel-title--dirty {
  background: rgba(var(--v-theme-warning), 0.06) !important;
}

.officer-row {
  display: grid;
  grid-template-columns: 90px 1fr 1fr;
  gap: 0;
  width: 100%;
  align-items: center;
}

.td-updated {
  text-align: center;
}

.td-name {
  padding: 0 8px;
  line-height: 1.3;
}

.td-id {
  padding: 0 8px;
}

.th-updated, .td-updated {
  text-align: center;
}

.status-chip {
  min-width: 52px;
  justify-content: center;
}

:deep(.officer-panel-content .v-expansion-panel-text__wrapper) {
  padding: 16px 20px;
  background: #f0f3f6;
  border-top: 2px solid #dce3eb;
}
</style>
